import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MarkEmailRead, ErrorOutline } from '@mui/icons-material';

// Dark theme colors matching the other auth pages.
const colors = {
  bgPrimary: '#0a0a0a',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  error: '#ef4444'
};

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: colors.bgPrimary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px'
});

const FormCard = styled(Box)({
  backgroundColor: colors.bgSurface,
  border: `1px solid ${colors.border}`,
  borderRadius: '16px',
  padding: '40px',
  maxWidth: '420px',
  width: '100%',
  textAlign: 'center'
});

const GradientButton = styled(Button)({
  background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '1rem',
  '&:hover': {
    background: `linear-gradient(135deg, ${colors.accentPrimary} 20%, ${colors.accentSecondary} 100%)`,
    boxShadow: `0 4px 20px rgba(59, 130, 246, 0.4)`,
  },
  '&:disabled': {
    background: colors.border,
    color: colors.textMuted,
  },
});

const VerifyEmailPage = () => {
  const { verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 'verifying' | 'success' | 'error'
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);
  // React 18 StrictMode mounts effects twice in dev; the token is single-use, so
  // guard against a double POST that would make the second call look "invalid".
  const attempted = useRef(false);

  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setStatus('error');
      setMessage('This verification link is missing its token.');
      return;
    }

    (async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        // Adopted the returned JWT; land the user in the app.
        setTimeout(() => navigate('/dashboard'), 1200);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.message || 'This verification link is invalid or has expired.');
      }
    })();
  }, [token, verifyEmail, navigate]);

  const handleResend = async () => {
    if (!resendEmail) {
      setResendMessage('Enter the email you signed up with.');
      return;
    }
    setResendMessage('');
    setResending(true);
    try {
      await resendVerification(resendEmail);
      setResendMessage('If your account still needs verifying, a new link is on its way.');
    } catch (err) {
      setResendMessage('Could not resend right now. Please try again shortly.');
    }
    setResending(false);
  };

  return (
    <PageWrapper>
      <Container maxWidth="sm">
        <FormCard>
          {status === 'verifying' && (
            <>
              <CircularProgress sx={{ color: colors.accentPrimary, mb: 3 }} />
              <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                Verifying your email…
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <MarkEmailRead sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1 }}>
                Email verified
              </Typography>
              <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                Taking you to your dashboard…
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <ErrorOutline sx={{ fontSize: 28, color: colors.error }} />
              </Box>
              <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1 }}>
                Verification failed
              </Typography>
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  textAlign: 'left',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: colors.error,
                  border: `1px solid ${colors.error}`,
                  '& .MuiAlert-icon': { color: colors.error }
                }}
              >
                {message}
              </Alert>

              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                Enter your email to get a fresh verification link.
              </Typography>
              <Box
                component="input"
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@example.com"
                sx={{
                  width: '100%',
                  padding: '12px 14px',
                  marginBottom: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgPrimary,
                  color: colors.textPrimary,
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <GradientButton fullWidth onClick={handleResend} disabled={resending} sx={{ mb: 2 }}>
                {resending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Resend verification email'}
              </GradientButton>
              {resendMessage && (
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                  {resendMessage}
                </Typography>
              )}

              <Link
                component={RouterLink}
                to="/login"
                sx={{
                  color: colors.accentPrimary,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Back to Sign In
              </Link>
            </>
          )}
        </FormCard>
      </Container>
    </PageWrapper>
  );
};

export default VerifyEmailPage;
